/**
 * Yup Schema Generator
 * Dynamically generates Yup validation schemas from CRUD formFields config
 */

import * as Yup from 'yup';

/**
 * Generate Yup validation schema from formFields configuration
 * @param {Array} formFields - Array of field configurations
 * @returns {Yup.ObjectSchema} - Yup validation schema
 */
export const generateYupSchema = (formFields) => {
  const schemaFields = {};

  formFields.forEach((field) => {
    let validator;

    switch (field.type) {
      case 'email':
        validator = Yup.string().email('Please enter a valid email address');
        break;

      case 'number':
        validator = Yup.number()
          .typeError(`${field.label} must be a number`)
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          );

        if (field.min !== undefined) {
          validator = validator.min(field.min, `${field.label} must be at least ${field.min}`);
        }
        if (field.max !== undefined) {
          validator = validator.max(field.max, `${field.label} must be at most ${field.max}`);
        }
        break;

      case 'date':
        validator = Yup.date()
          .typeError(`${field.label} must be a valid date`)
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          );
        break;

      case 'select':
        validator = Yup.string().nullable();
        break;

        case 'multi-append':
        case 'multi-select':
        validator = Yup.array().of(Yup.string());
        break;
      

      case 'textarea':
      case 'richtext':
        validator = Yup.string();
        if (field.maxLength) {
          validator = validator.max(field.maxLength, `${field.label} must be at most ${field.maxLength} characters`);
        }
        break;

      case 'tel':
      case 'phone':
        validator = Yup.string().matches(
          /^[0-9]{10,15}$/,
          'Please enter a valid phone number'
        );
        break;

      case 'url':
        validator = Yup.string().url('Please enter a valid URL');
        break;

      default:
        validator = Yup.string();
        if (field.minLength) {
          validator = validator.min(field.minLength, `${field.label} must be at least ${field.minLength} characters`);
        }
        if (field.maxLength) {
          validator = validator.max(field.maxLength, `${field.label} must be at most ${field.maxLength} characters`);
        }
    }

    // Apply required validation
    if (field.required) {
      if (field.type === 'number') {
        validator = validator.required(`${field.label} is required`);
      } else if (field.type === 'date') {
        validator = validator.required(`${field.label} is required`);
      } else if (field.type === 'select') {
        validator = validator.required(`Please select ${field.label.toLowerCase()}`);
      } else {
        validator = validator.required(`${field.label} is required`);
      }
    }

    // Apply conditional required (e.g. required only when status === 'COMPLETED')
    if (field.requiredWhen && typeof field.requiredWhen === 'function') {
      const requiredWhenFn = field.requiredWhen;
      const requiredWhenMsg = field.requiredWhenMessage || `${field.label} is required`;
      validator = validator.test(
        `required-when-${field.name}`,
        requiredWhenMsg,
        function (value) {
          if (requiredWhenFn(this.parent)) {
            return value !== null && value !== undefined && value !== '';
          }
          return true;
        }
      );
    }

    // Apply custom validation if provided
    if (field.validate && typeof field.validate === 'function') {
      validator = validator.test(
        'custom-validation',
        field.validationMessage || 'Invalid value',
        field.validate
      );
    }

    schemaFields[field.name] = validator;
  });

  return Yup.object().shape(schemaFields);
};

/**
 * Get initial values from formFields config
 * @param {Array} formFields - Array of field configurations
 * @param {Object} existingData - Existing data for edit mode
 * @returns {Object} - Initial form values
 */
export const getInitialValues = (formFields, existingData = {}) => {
  const initialValues = {};

  formFields.forEach((field) => {
    if (existingData && existingData[field.name] !== undefined) {
      initialValues[field.name] = existingData[field.name];
    } else if (field.default !== undefined) {
      initialValues[field.name] = field.default;
    } else {
      // Set default empty values based on type
      switch (field.type) {
        case 'number':
          initialValues[field.name] = '';
          break;
        case 'select':
          initialValues[field.name] = '';
          break;
        case 'multi-append':
        case 'multi-select':
          initialValues[field.name] = [];
          break;
        case 'date':
          initialValues[field.name] = '';
          break;
        default:
          initialValues[field.name] = '';
      }
    }
  });

  return initialValues;
};

export default {
  generateYupSchema,
  getInitialValues,
};
